import { For, Show, createMemo, createSignal } from 'solid-js';
import type { ProjectManifest } from '../../types/project';
import { projectDisplayTitle } from '../../types/project';
import { ProjectIcon } from '../ProjectIcon';

interface ProjectExplorerWindowProps {
  projects: ProjectManifest[];
  openProject: (project: ProjectManifest) => void;
}

function resolveMedia(project: ProjectManifest, mediaPath?: string): string | undefined {
  if (!mediaPath || !project.runtimeUrl) return undefined;
  try {
    return new URL(mediaPath, project.runtimeUrl).toString();
  } catch {
    return undefined;
  }
}

export function ProjectExplorerWindow(props: ProjectExplorerWindowProps) {
  const [category, setCategory] = createSignal('All Projects');
  const [query, setQuery] = createSignal('');
  const [selectedSlug, setSelectedSlug] = createSignal<string>();

  const published = createMemo(() => props.projects.filter((project) => project.status === 'published'));
  const categories = createMemo(() => ['All Projects', ...new Set(published().map((project) => project.category))]);
  const visible = createMemo(() => {
    const search = query().trim().toLowerCase();
    return published().filter((project) => {
      const inCategory = category() === 'All Projects' || project.category === category();
      const haystack = [project.title, project.summary, project.category, ...(project.tags || [])].join(' ').toLowerCase();
      return inCategory && (!search || haystack.includes(search));
    });
  });
  const selected = createMemo(() => {
    const explicit = visible().find((project) => project.slug === selectedSlug());
    return explicit || visible()[0];
  });

  return (
    <div class="explorer-window">
      <div class="explorer-toolbar">
        <button disabled title="Back">←</button>
        <button disabled title="Forward">→</button>
        <span class="toolbar-separator" />
        <label for="project-search">Search:</label>
        <input
          id="project-search"
          type="search"
          value={query()}
          onInput={(event) => setQuery(event.currentTarget.value)}
          placeholder="Find a project"
        />
      </div>

      <div class="explorer-address field-row">
        <label for="project-address">Address</label>
        <input id="project-address" value={`C:\\Ivan\\Projects\\${category()}`} readOnly />
      </div>

      <div class="explorer-main">
        <aside class="explorer-tree sunken-panel">
          <ul class="tree-view">
            <li>
              <details open>
                <summary>My Projects</summary>
                <ul>
                  <For each={categories()}>
                    {(item) => (
                      <li>
                        <button
                          class="tree-button"
                          classList={{ selected: category() === item }}
                          onClick={() => {
                            setCategory(item);
                            setSelectedSlug(undefined);
                          }}
                        >
                          {item}
                        </button>
                      </li>
                    )}
                  </For>
                </ul>
              </details>
            </li>
          </ul>
        </aside>

        <section class="explorer-files sunken-panel" aria-label="Projects">
          <div class="file-list-header">
            <span>Name</span><span>Category</span><span>Year</span>
          </div>
          <For each={visible()} fallback={<p class="empty-state">No projects match this view.</p>}>
            {(project) => (
              <button
                class="file-row"
                classList={{ selected: selected()?.slug === project.slug }}
                onClick={() => setSelectedSlug(project.slug)}
                onDblClick={() => props.openProject(project)}
              >
                <span class="file-name">
                  <ProjectIcon project={project} size={22} />
                  {projectDisplayTitle(project)}
                </span>
                <span>{project.category}</span>
                <span>{project.year || '—'}</span>
              </button>
            )}
          </For>
        </section>

        <aside class="explorer-details sunken-panel">
          <Show when={selected()} keyed fallback={<p>Select a project.</p>}>
            {(project) => {
              const preview = resolveMedia(project, project.media?.preview);
              return (
                <>
                  <Show when={preview}>
                    {(url) => <img class="project-preview" src={url()} alt="" />}
                  </Show>
                  <div class="project-detail-heading">
                    <ProjectIcon project={project} size={36} />
                    <div>
                      <h2>{projectDisplayTitle(project)}</h2>
                      <small>{project.category}</small>
                    </div>
                  </div>
                  <p>{project.summary}</p>
                  <Show when={project.tags?.length}>
                    <div class="tag-list"><For each={project.tags}>{(tag) => <span>{tag}</span>}</For></div>
                  </Show>
                  <div class="details-actions">
                    <button class="default" onClick={() => props.openProject(project)}>Open</button>
                    <Show when={project.sourceUrl}>
                      {(url) => <button onClick={() => window.open(url(), '_blank', 'noopener,noreferrer')}>Source</button>}
                    </Show>
                  </div>
                </>
              );
            }}
          </Show>
        </aside>
      </div>

      <div class="status-bar">
        <p class="status-bar-field">{visible().length} object{visible().length === 1 ? '' : 's'}</p>
        <p class="status-bar-field">Double-click to run</p>
        <p class="status-bar-field">Source available where noted</p>
      </div>
    </div>
  );
}
